import { authenticationToken } from "./utils";

export const BASE_URL = process.env.REACT_APP_API_URL;

// IMAGES

export const getImage = async (id: string) => {
	const response = await fetch(
		`${BASE_URL}/data/getGalleryImage?imageId=` + id,
		{
			method: "GET",
		},
	);
	if (!response.ok) {
		return null;
	}
	try {
		const data = await response.json();
		return data;
	} catch (error) {
		throw error;
		return null;
	}
};

export const getImageFromDirectory = async (
	name: string,
	directory: string,
) => {
	const response = await fetch(
		`${BASE_URL}/data/getImage?name=` + name + "&directory=" + directory,
		{
			method: "GET",
		},
	);
	if (!response.ok) {
		return null;
	}
	try {
		const data = await response.json();
		return data;
	} catch (error) {
		throw error;
		return null;
	}
};

export const sendImage = async (id: string, image: File) => {
	const data = new FormData();
	data.append("image", image);
	const response = await fetch(
		`${BASE_URL}/admin/edit/editImage?imageId=` + id,
		{
			method: "POST",
			headers: {
				Authorization: `Bearer ${authenticationToken()}`,
			},
			body: data,
		},
	);
	if (!response.ok) {
		throw new Error("Unable to edit image");
		createImage(id, image);
	}
};

export const createImage = async (id: string, image: File) => {
	const data = new FormData();
	data.append("image", image);
	const response = await fetch(
		`${BASE_URL}/admin/edit/addImage?imageId=` + id,
		{
			method: "POST",
			headers: {
				Authorization: `Bearer ${authenticationToken()}`,
			},
			body: data,
		},
	);
	if (!response.ok) {
		throw new Error("Unable to add image");
	}
};

// AUTHENTICATION

export type Credentials = {
	mail: string;
	password: string;
};

export type AuthenticationResponse = {
	authenticationToken: string;
	refreshToken: string;
	role: string;
	email: string;
};

export const authenticate = async (
	mail: string,
	password: string,
): Promise<AuthenticationResponse | null> => {
	const credentials: Credentials = { mail, password };
	const response = await fetch(`${BASE_URL}/auth-service/authenticate`, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			Accept: "application/json",
		},
		body: JSON.stringify(credentials),
	});
	if (!response.ok) {
		throw new Error("Unable to authenticate");
		return null;
	}
	try {
		const data: AuthenticationResponse = await response.json();
		if (
			data.authenticationToken &&
			data.refreshToken &&
			data.email &&
			data.role
		) {
			return data;
		}
	} catch (error) {
		throw error;
		return null;
	}
	return null;
};

// PRODUCTS

export type AddProductType = {
	name: string;
	price: number;
	ingredients: string[];
	categoryLabel: string;
	subcategoryLabel?: string;
	featured: boolean;
};

export type ProductType = {
	name: string;
	price: number;
	ingredients: string[];
	id: string;
	index?: number;
};

export type Subcategory = {
	label: string;
	products: ProductType[];
};

export type Category = {
	label: string;
	subcategories: Subcategory[];
	products: ProductType[];
};

export type Menu = {
	categories: Category[];
};

export type Featured = {
	products: ProductType[];
};

export const getMenu = async (): Promise<Menu> => {
	const response = await fetch(`${BASE_URL}/data/menu`, {
		method: "GET",
	});
	if (!response.ok) {
		throw new Error("unable to fetch menu");
	}
	const data = await response.json();
	return data;
};

export const getFeatured = async (): Promise<Featured> => {
	const response = await fetch(`${BASE_URL}/data/featured`, {
		method: "GET",
	});
	if (!response.ok) {
		throw new Error("unable to fetch featured");
	}
	const data = await response.json();
	return data;
};

export const editProduct = async (product: ProductType): Promise<boolean> => {
	const response = await fetch(`${BASE_URL}/admin/edit/editProduct`, {
		method: "POST",
		headers: {
			Authorization: `Bearer ${authenticationToken()}`,
			"Content-Type": "application/json",
			Accept: "application/json",
		},
		body: JSON.stringify(product),
	});
	return response.ok;
};

export const moveProductUp = async (id: string) => {
	const response = await fetch(`${BASE_URL}/admin/edit/moveUp?id=${id}`, {
		method: "POST",
		headers: {
			Authorization: `Bearer ${authenticationToken()}`,
			"Content-Type": "application/json",
			Accept: "application/json",
		},
	});
};

export const moveProductDown = async (id: string) => {
	const response = await fetch(`${BASE_URL}/admin/edit/moveDown?id=${id}`, {
		method: "POST",
		headers: {
			Authorization: `Bearer ${authenticationToken()}`,
			"Content-Type": "application/json",
			Accept: "application/json",
		},
	});
};

export const addProduct = async (
	product: AddProductType,
): Promise<ProductType> => {
	const response = await fetch(`${BASE_URL}/admin/edit/addProduct`, {
		method: "POST",
		headers: {
			Authorization: `Bearer ${authenticationToken()}`,
			"Content-Type": "application/json",
			Accept: "application/json",
		},
		body: JSON.stringify(product),
	});
	if (!response.ok) {
		throw new Error("Failed to add product");
	}
	return response.json();
};

export const deleteProduct = async (id: string): Promise<boolean> => {
	const response = await fetch(
		`${BASE_URL}/admin/edit/removeProduct?id=` + id,
		{
			method: "POST",
			headers: {
				Authorization: `Bearer ${authenticationToken()}`,
				"Content-Type": "application/json",
				Accept: "application/json",
			},
		},
	);
	return response.ok;
};

// EVENTS

export type EventType = {
	id: string;
	title: string;
	description: string;
	date: string;
	imageName: string;
};

export type AddEventType = {
	title: string;
	description: string;
	date: string;
};

export const addEvent = async (
	event: AddEventType,
	image?: File,
): Promise<boolean> => {
	const data = new FormData();
	data.append(
		"request",
		new Blob([JSON.stringify(event)], { type: "application/json" }),
	);
	if (image) {
		data.append("image", image);
	}
	const response = await fetch(`${BASE_URL}/admin/edit/addEvent`, {
		method: "POST",
		headers: {
			Authorization: `Bearer ${authenticationToken()}`,
		},
		body: data,
	});
	return response.ok;
};

export const getEvents = async (): Promise<EventType[]> => {
	const response = await fetch(`${BASE_URL}/data/events`, {
		method: "GET",
	});
	if (!response.ok) {
		throw new Error("Unable to fetch events");
		return [];
	}
	const data = await response.json();
	return data;
};

export const deleteEvent = async (id: string): Promise<boolean> => {
	const response = await fetch(
		`${BASE_URL}/admin/edit/removeEvent?id=` + id,
		{
			method: "POST",
			headers: {
				Authorization: `Bearer ${authenticationToken()}`,
			},
		},
	);
	return response.ok;
};

export const editEvent = async (event: EventType): Promise<boolean> => {
	const response = await fetch(`${BASE_URL}/admin/edit/editEvent`, {
		method: "POST",
		headers: {
			Authorization: `Bearer ${authenticationToken()}`,
			"Content-Type": "application/json",
			Accept: "application/json",
		},
		body: JSON.stringify(event),
	});
	return response.ok;
};

// EMAILS

export const sendBooking = async (content: string): Promise<boolean> => {
	const response = await fetch(`${BASE_URL}/mail/booking`, {
		method: "POST",
		body: content,
	});
	return response.ok;
};

export const sendContact = async (content: string): Promise<boolean> => {
	const response = await fetch(`${BASE_URL}/mail/contact`, {
		method: "POST",
		body: content,
	});
	return response.ok;
};

// SCHEDULE

export type DayType = {
	name: string;
	open: string;
	close: string;
};

export const getSchedule = async (name: string): Promise<DayType | null> => {
	const response = await fetch(`${BASE_URL}/data/getSchedule?day=` + name);
	if (response.ok) {
		const data = await response.json();
		return data;
	} else {
		throw new Error("Unable to fetch schedule");
	}
};

export const editSchedule = async (schedule: DayType): Promise<boolean> => {
	const response = await fetch(`${BASE_URL}/admin/edit/editSchedule`, {
		method: "POST",
		headers: {
			Authorization: `Bearer ${authenticationToken()}`,
			"Content-Type": "application/json",
			Accept: "application/json",
		},
		body: JSON.stringify(schedule),
	});
	return response.ok;
};
